import ms from 'ms'
import fetch from '@adc/fetch'
import configment from '@adc/configment'
import { UAParser } from 'ua-parser-js'

// NOTE: If this becomes an issue, we can make it configurable
const INSTRUMENTATION_PENDING_METRICS_FLUSH_THRESHOLD = 10

/**
 * Two conditions for a flush:
 * 1) Time-based. Too much time has elapsed before we last flushed metrics. Active flushing is checking when a metric is
 *    appended, and passive flushing is on a background interval.
 * 2) Volume-based. We want to batch requests up to not lose events.
 *
 * We specifically do not set lastFlushedAt to any to begin with so metrics are stored on the leading edge instead of
 * waiting for the time-base cycle to become valid.
 */
let flusherInterval = null
let activelyFlushing = false
let lastFlushedMetricsAt = null
let lastFlushedDomainEventsAt = null
let pendingMetrics = []
let pendingDomainEvents = []
let postUrl = ''

const initialize = (initOptions = {}) => {
  flusherInterval = window.setInterval(
    flush.bind(null, { force: true }),
    ms('2m')
  )
  postUrl = initOptions.url
}

const haveMetrics = () => pendingMetrics.length || pendingDomainEvents.length

// Create an object containing copies of the references to the pending metrics and domain events.
const getCommitted = () => ({
  pendingMetrics,
  pendingDomainEvents,
})

const doneFlushing = committed => {
  activelyFlushing = false

  if (!committed) {
    committed = getCommitted()
  }

  if (!haveMetrics()) return

  if (committed.pendingMetrics.length > 0) {
    lastFlushedMetricsAt = Date.now()
  }

  if (committed.pendingDomainEvents.length > 0) {
    lastFlushedDomainEventsAt = Date.now()
  }
}

const xhr = () => {
  if (activelyFlushing) return

  const payload = createPayload()
  if (!payload) {
    return
  }

  activelyFlushing = true

  // Transfer references to the arrays before we overwrite them. We will send these and update the last-flushed times
  // after the fetch completes based on their content.
  const committed = getCommitted()
  pendingMetrics = []
  pendingDomainEvents = []

  fetch
    .post(postUrl, {
      // This is always just best effort. We retry once and that's a wrap. Instrumentation shouldn't negatively impact the
      // application
      tries: 2,
      body: payload,
    })
    .finally(doneFlushing(committed))
}

const flush = () => {
  if (!haveMetrics()) return

  window.queueMicrotask(xhr)
}

const createPayload = () =>
  pendingMetrics.length === 0 && pendingDomainEvents.length === 0
    ? null
    : {
        app_source: configment.get('APP_NAME'),
        ...(pendingMetrics.length > 0 ? { metrics: pendingMetrics } : {}),
        ...(pendingDomainEvents.length > 0
          ? { domainEvents: pendingDomainEvents }
          : {}),
      }

/**
 * Collects page view data for instrumentation in web or React Native.
 * Web: Requires ua-parser-js (CDN: https://unpkg.com/ua-parser-js@1.0.35/dist/ua-parser.min.js).
 * React Native: Requires all relevant data to be passed via options.
 * @param {Object} [options] - Optional parameters for web and React Native.
 * @param {string} [options.client_type] - Client type (e.g., "web", "native").
 * @param {string} [options.device_id] - React Native physical device ID (e.g., D47F400B-33B5-4808-838A-12A75FB6D2E8).
 * @param {string} [options.device_info] - React Native device model (e.g., "Apple iPhone 14 Pro Max").
 * @param {string} [options.device_type] - React Native device type (e.g., "mobile", "tablet").
 * @param {string} [options.host_name] - React Native host name (e.g., "auction.com", "smartsale.com").
 * @param {string} [options.os] - React Native OS name and version (e.g., "ios 18.4", "android 14").
 * @param {string} [options.page_path] - Web page path (e.g., "/product/123") or React Native route screen name: (e.g., SearchScreen).
 * @param {string} [options.page_search_params] - Web page search parameters (e.g., "?q=search") or React Native route parameters.
 * @param {string} [options.screen_resolution] - Web or React Native screen resolution (e.g., "375x812").
 * @returns {DomainEventPageViewData} Object conforming to the DomainEventPageViewData type.
 */
function collectDomainEventPageViewData({
  client_type = 'web',
  device_id,
  device_info,
  device_type,
  host_name,
  os,
  page_path,
  page_search_params,
  screen_resolution,
} = {}) {
  // Determine client type
  if (!page_search_params) page_search_params = {}
  let referrer, browser, page_title

  if (client_type === 'web') {
    // page_path
    page_path = window.location.pathname
    const urlParams = new URLSearchParams(window.location.search)
    for (const [key, value] of urlParams.entries()) {
      page_search_params[key] = value || undefined
    }
    referrer = document.referrer || undefined

    const uaResult = UAParser(navigator.userAgent)
    device_type = uaResult.device.type || 'other'
    browser = uaResult.browser.name || 'unknown'
    os = `${uaResult.os?.name || 'Unknown'} ${
      uaResult.os?.version || ''
    }`.trim()
    screen_resolution = `${window.screen.width}x${window.screen.height}`
    page_title = window.document.title
    host_name = host_name || location.host
  } else {
    referrer = undefined
    browser = 'unknown'
    host_name = host_name || 'unknown'
  }

  /** @type {DomainEventPageViewData} */
  const data = {
    page_path,
    page_search_params,
    referrer,
    device_id,
    device_info,
    device_type,
    browser,
    os,
    screen_resolution,
    client_type,
    page_title,
    host_name,
  }
  return data
}

const appendMetric = ({ metric, domainEvent }) => {
  if (!configment.get('INSTRUMENT_ENABLE', true)) {
    return
  }

  if (metric) {
    pendingMetrics.push(metric)
  } else if (domainEvent) {
    domainEvent.service ||= configment.get('APP_NAME')
    if (!domainEvent.data) {
      domainEvent.data = {}
    }
    domainEvent.data.client_time ||= new Date().toISOString()
    Object.assign(
      domainEvent.data,
      collectDomainEventPageViewData(domainEvent.data)
    )
    pendingDomainEvents.push(domainEvent)
  }

  const fullQueue =
    pendingMetrics.length > INSTRUMENTATION_PENDING_METRICS_FLUSH_THRESHOLD ||
    pendingDomainEvents.length > INSTRUMENTATION_PENDING_METRICS_FLUSH_THRESHOLD
  const staleQueue =
    Date.now() - lastFlushedMetricsAt > ms('30s') ||
    Date.now() - lastFlushedDomainEventsAt > ms('30s')

  if (fullQueue || staleQueue) {
    flush()
  }
}

function instrument(metric, tagsOrFunc, maybeFunc) {
  return (maybeFunc || tagsOrFunc)()
}

const createInstrumentFn = initialize => {
  // Attach exports to the default export. This API predates moving to modules, which is why these aren't named exports
  // here.
  // @deprecated - use instrument.initialize instead
  instrument.init = initialize
  instrument.initialize = initialize
  instrument.increment = (name, value = 1, tags = []) =>
    appendMetric({ metric: { type: 'increment', name, value, tags } })
  instrument.decrement = (name, value = 1, tags = []) =>
    appendMetric({ metric: { type: 'decrement', name, value, tags } })
  instrument.gauge = (name, value, tags = []) =>
    appendMetric({ metric: { type: 'gauge', name, value, tags } })
  instrument.histogram = (name, value, tags = []) =>
    appendMetric({ metric: { type: 'histogram', name, value, tags } })
  instrument.domainEvent = domainEvent => appendMetric({ domainEvent })

  return instrument
}

export {
  initialize,
  createPayload,
  haveMetrics,
  doneFlushing,
  xhr,
  createInstrumentFn,
}
